import { useMemo } from 'react';
import {
  filterNavigationOptionsByPermissionsPropertyClass,
  determineNavigationToOnClick,
  getOptionNotificationsBadgeType,
} from './utils';
import useIntl from '../../hooks/useIntl';
import { NavigationOption } from '../../containers/App/types.js';

const navOverrideMessageId = (name) =>
  `App.AppBarNavDrawer.nav.${name.replace(/\s+/g, '')}`;

const useNavigationOptions = ({
  navigationOptions,
  selectedProperty,
  permissions,
  paymentProvider,
  flags,
  selectedPropertySubjournals,
  locale,
  isXs,
  history,
  setOpen,
  twoWayNotifications,
  hasActiveTwoWayProperties,
}: {
  navigationOptions: NavigationOption[],
  selectedProperty: Object,
  permissions: string[],
  paymentProvider: string,
  flags: Object,
  locale: string,
}): Object => {
  const intl = useIntl();
  return useMemo(() => {
    const { pathname } = history.location;

    const filteredOptions = filterNavigationOptionsByPermissionsPropertyClass({
      navigationOptions,
      userPermissions: permissions,
      selectedProperty,
      paymentProvider,
      flags,
      selectedPropertySubjournals,
      hasActiveTwoWayProperties,
    });

    // if order === -1 then it will be placed at the end of the list
    const sortedOptions = filteredOptions.sort((a, b) => {
      if (a.order === -1) {
        return 1;
      }
      if (b.order === -1) {
        return -1;
      }
      return a.order - b.order;
    });

    const messagingHubEnabled = flags?.messagingHub === true;
    const resolveLabel = (optionName, optionTranslations) => {
      if (messagingHubEnabled) {
        const overrideId = navOverrideMessageId(optionName);
        if (intl.messages?.[overrideId]) {
          return intl.formatMessage({ id: overrideId });
        }
      }
      return optionTranslations[locale];
    };

    const navOptionsArray = [];
    sortedOptions.forEach(({ name, translations, openTab, url, children }) => {
      const navOption = {
        text: resolveLabel(name, translations),
        value: name,
        selected: pathname === url,
        notification: getOptionNotificationsBadgeType(
          twoWayNotifications,
          name,
        ),
        ...(!Boolean(children?.length) &&
          determineNavigationToOnClick({
            url,
            openTab,
            history,
            setOpen,
            isXs,
          })),
      };

      const subOptions = children.map((subOption) => {
        const selected = pathname === subOption.url;

        if (selected) {
          navOption.selected = true;
        }

        return {
          text: resolveLabel(subOption.name, subOption.translations),
          value: subOption.name,
          selected,
          notification: getOptionNotificationsBadgeType(
            twoWayNotifications,
            subOption.name,
          ),
          ...determineNavigationToOnClick({
            url: subOption.url,
            openTab: subOption.openTab,
            history,
            setOpen,
            isXs,
          }),
        };
      });
      navOption.subOptions = subOptions ?? [];
      navOptionsArray.push(navOption);
    });

    return navOptionsArray;
  }, [
    history,
    navigationOptions,
    permissions,
    selectedProperty,
    paymentProvider,
    flags,
    selectedPropertySubjournals,
    locale,
    twoWayNotifications,
    setOpen,
    isXs,
    intl,
    hasActiveTwoWayProperties,
  ]);
};

export default useNavigationOptions;
